import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import useSwitchChild from '@/hooks/useSwitchChild';

function childOptionLabel(child) {
	const name = child.member?.name || `Child #${child.id}`;
	const klass = child.member?.on_list_attributes?.class;
	const label = klass ? `${name} · ${klass}` : name;
	const inactive = child.family_is_active === false || child.is_active === false;
	return inactive ? `${label} (Inactive)` : label;
}

/**
 * Header control to switch the active child. Uses the shared switchChild handler.
 * @param {{ label?: string, singlePrefix?: string, variant?: 'mui' | 'native' }} props
 */
export default function ChildSelector({
	label = 'Active child',
	singlePrefix = 'Ordering for',
	variant = 'mui',
} = {}) {
	const { switchChild, switchingLocked, selectedCustomerId, children } = useSwitchChild();

	if (!children.length) return null;

	if (children.length === 1) {
		const only = children[0];
		const name = only.member?.name || `Child #${only.id}`;
		if (variant === 'native') {
			return (
				<label className='ordering-child' data-testid='child-selector-single'>
					{label}
					<select aria-label='Order for child' disabled value={String(only.id)}>
						<option value={String(only.id)}>{childOptionLabel(only)}</option>
					</select>
				</label>
			);
		}
		return (
			<p className='text-xs' data-testid='child-selector-single'>
				{singlePrefix} <span className='font-bold'>{name}</span>
			</p>
		);
	}

	const value = selectedCustomerId != null ? String(selectedCustomerId) : '';

	if (variant === 'native') {
		return (
			<label className='ordering-child'>
				{label}
				<select
					aria-label='Order for child'
					value={value}
					disabled={switchingLocked}
					onChange={(event) => switchChild(event.target.value)}
				>
					{children.map((child) => (
						<option key={child.id} value={String(child.id)}>
							{childOptionLabel(child)}
						</option>
					))}
				</select>
			</label>
		);
	}

	const labelId = 'child-selector-label';

	return (
		<FormControl size='small' fullWidth sx={{ mt: 0.5 }}>
			<InputLabel id={labelId}>{label}</InputLabel>
			<Select
				labelId={labelId}
				id='child-selector'
				label={label}
				value={value}
				disabled={switchingLocked}
				onChange={(event) => {
					switchChild(event.target.value);
				}}
				inputProps={{ 'aria-label': label }}
			>
				{children.map((child) => (
					<MenuItem key={child.id} value={String(child.id)}>
						{childOptionLabel(child)}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}
